import type { ComponentChildren } from 'preact';
import type { IllustrationProps } from './types';

export const ILLUSTRATION_PALETTE = {
  primary: '#3b82f6',
  sky: '#e0f2fe',
  skyLight: '#f0f9ff',
  ground: '#93c5fd',
  groundDark: '#60a5fa',
  stroke: '#1e3a8a',
  strokeMuted: '#475569',
  easy: '#22c55e',
  struggle: '#f59e0b',
  impossible: '#ef4444',
  figure: '#1e40af',
  wheel: '#334155',
  window: '#bfdbfe',
} as const;

export const DEFAULT_VIEWBOX = '0 0 80 56';

type SvgRootProps = IllustrationProps & {
  children: ComponentChildren;
  title?: string;
};

export function IllustrationSvg({
  className,
  width = 80,
  height = 56,
  children,
  title,
}: SvgRootProps) {
  return (
    <svg
      viewBox={DEFAULT_VIEWBOX}
      width={width}
      height={height}
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-hidden={title ? undefined : true}
      aria-label={title}
    >
      {children}
    </svg>
  );
}

type SkyBackdropProps = {
  accent?: string;
};

export function SkyBackdrop({ accent = ILLUSTRATION_PALETTE.sky }: SkyBackdropProps) {
  return (
    <>
      <rect x="0" y="0" width="80" height="56" fill={ILLUSTRATION_PALETTE.skyLight} rx="4" />
      <rect x="0" y="0" width="80" height="30" fill={accent} opacity="0.12" />
    </>
  );
}

type SlopeSurfaceProps = {
  startY: number;
  endY: number;
  fill?: string;
  accent?: string;
};

/** Eğimli zemin yüzeyi — tüm sahnelerde aynı çizgi dili */
export function SlopeSurface({
  startY,
  endY,
  fill = ILLUSTRATION_PALETTE.ground,
  accent = ILLUSTRATION_PALETTE.groundDark,
}: SlopeSurfaceProps) {
  return (
    <>
      <path
        d={`M6 ${startY} L74 ${endY} L74 54 L6 54 Z`}
        fill={fill}
        stroke={ILLUSTRATION_PALETTE.stroke}
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
      <path
        d={`M6 ${startY} L74 ${endY}`}
        stroke={accent}
        strokeWidth="2"
        strokeLinecap="round"
      />
    </>
  );
}

type WalkerPosture = 'easy' | 'tired' | 'impossible';

type WalkerFigureProps = {
  cx: number;
  cy: number;
  posture: WalkerPosture;
  primary?: string;
};

export function WalkerFigure({
  cx,
  cy,
  posture,
  primary = ILLUSTRATION_PALETTE.figure,
}: WalkerFigureProps) {
  const lean = posture === 'easy' ? 0 : posture === 'tired' ? 14 : 28;
  const headY = cy - 16;
  const shoulderY = cy - 8;
  const hipY = cy + 2;
  const legSpread = posture === 'easy' ? 6 : posture === 'tired' ? 4 : 2;

  return (
    <g transform={`translate(${cx} ${cy}) rotate(${-lean})`} stroke={primary} strokeWidth="1.6" strokeLinecap="round" fill="none">
      <circle cx="0" cy={headY - cy} r="3.2" fill={primary} stroke="none" />
      <line x1="0" y1={headY - cy + 3} x2="0" y2={hipY - cy} />
      <line x1="0" y1={shoulderY - cy} x2="-5" y2={hipY - cy - 2} />
      <line x1="0" y1={shoulderY - cy} x2="5" y2={hipY - cy - 4} />
      <line x1="0" y1={hipY - cy} x2={-legSpread} y2={hipY - cy + 10} />
      <line x1="0" y1={hipY - cy} x2={legSpread + (posture === 'easy' ? 3 : 0)} y2={hipY - cy + 10} />
    </g>
  );
}

type CarFigureProps = {
  cx: number;
  cy: number;
  tilt?: number;
  slip?: boolean;
  primary?: string;
  accent?: string;
};

export function CarFigure({
  cx,
  cy,
  tilt = 0,
  slip = false,
  primary = ILLUSTRATION_PALETTE.primary,
  accent = ILLUSTRATION_PALETTE.stroke,
}: CarFigureProps) {
  return (
    <g transform={`translate(${cx} ${cy}) rotate(${tilt})`}>
      <rect x="-14" y="-8" width="28" height="10" rx="2.5" fill={primary} stroke={accent} strokeWidth="1.2" />
      <path d="M-6 -8 L-2 -13 L8 -13 L12 -8 Z" fill={ILLUSTRATION_PALETTE.window} stroke={accent} strokeWidth="1" strokeLinejoin="round" />
      <circle cx="-8" cy="4" r="3.2" fill={ILLUSTRATION_PALETTE.wheel} />
      <circle cx="8" cy="4" r="3.2" fill={ILLUSTRATION_PALETTE.wheel} />
      {slip ? (
        <g stroke={accent} strokeWidth="1" strokeLinecap="round" opacity="0.7">
          <line x1="-18" y1="6" x2="-12" y2="6" />
          <line x1="-20" y1="8" x2="-14" y2="8" />
        </g>
      ) : null}
    </g>
  );
}

type TractorFigureProps = {
  cx: number;
  cy: number;
  tilt?: number;
  slip?: boolean;
  primary?: string;
  accent?: string;
};

export function TractorFigure({
  cx,
  cy,
  tilt = 0,
  slip = false,
  primary = ILLUSTRATION_PALETTE.primary,
  accent = ILLUSTRATION_PALETTE.stroke,
}: TractorFigureProps) {
  return (
    <g transform={`translate(${cx} ${cy}) rotate(${tilt})`}>
      <rect x="-4" y="-14" width="10" height="8" rx="1.5" fill={ILLUSTRATION_PALETTE.window} stroke={accent} strokeWidth="1" />
      <rect x="-16" y="-6" width="24" height="9" rx="2" fill={primary} stroke={accent} strokeWidth="1.2" />
      <rect x="8" y="-4" width="8" height="5" rx="1" fill={accent} opacity="0.35" />
      <circle cx="-10" cy="5" r="4" fill={ILLUSTRATION_PALETTE.wheel} />
      <circle cx="10" cy="5" r="5" fill={ILLUSTRATION_PALETTE.wheel} />
      <line x1="-2" y1="-14" x2="-2" y2="-18" stroke={accent} strokeWidth="1.4" strokeLinecap="round" />
      {slip ? (
        <g stroke={accent} strokeWidth="1" strokeLinecap="round" opacity="0.7">
          <line x1="-22" y1="7" x2="-16" y2="7" />
          <line x1="-24" y1="9" x2="-18" y2="9" />
        </g>
      ) : null}
    </g>
  );
}

export function MotionLines({ x, y, count = 2 }: { x: number; y: number; count?: number }) {
  return (
    <g stroke={ILLUSTRATION_PALETTE.strokeMuted} strokeWidth="1" strokeLinecap="round" opacity="0.55">
      {Array.from({ length: count }, (_, i) => (
        <line key={i} x1={x - i * 4} y1={y + i * 3} x2={x - 6 - i * 4} y2={y + i * 3} />
      ))}
    </g>
  );
}

export function StruggleMark({ x, y }: { x: number; y: number }) {
  return (
    <g stroke={ILLUSTRATION_PALETTE.struggle} strokeWidth="1.4" strokeLinecap="round">
      <line x1={x} y1={y} x2={x + 4} y2={y + 4} />
      <line x1={x + 4} y1={y} x2={x} y2={y + 4} />
    </g>
  );
}

export function WarningDot({ x, y }: { x: number; y: number }) {
  return <circle cx={x} cy={y} r="2.2" fill={ILLUSTRATION_PALETTE.impossible} />;
}
